import React from 'react';
import { Link } from 'react-router-dom';
import { Home, Stethoscope, BookOpen, PoundSterling, ArrowRight } from 'lucide-react';
import SEO from './SEO';
import { useBookingModal } from '../context/BookingContext';
import { Button } from './ui/button';

/**
 * 404 Not Found — shown for genuinely dead / mistyped URLs (the router
 * catch-all). Emits `noindex` so Google drops these instead of logging them
 * as soft-404s or redirects. Real legacy URLs still 301-style redirect via
 * their own routes above the catch-all, so they never reach here.
 */
const QUICK_LINKS = [
  { to: '/services/tmd-treatment', label: 'TMD & jaw pain treatment', icon: Stethoscope },
  { to: '/fees', label: 'Fees & pricing', icon: PoundSterling },
  { to: '/tmd-knowledge-base', label: 'Knowledge base', icon: BookOpen },
];

export default function NotFound() {
  const { openBooking } = useBookingModal();

  return (
    <>
      <SEO
        title="Page not found — The TMD Physio"
        description="Sorry, that page could not be found. Explore our TMD physiotherapy services, fees and knowledge base, or book an appointment."
        path="/404"
        noindex
      />
      <main
        className="min-h-[70vh] flex items-center justify-center px-6 py-20 bg-[#f0fdfa]"
        data-testid="not-found-page"
      >
        <div className="max-w-xl w-full text-left">
          <span className="inline-block text-sm font-semibold tracking-widest uppercase text-teal-700">
            Error 404
          </span>
          <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold text-slate-900 leading-tight">
            We couldn&rsquo;t find that page.
          </h1>
          <p className="mt-4 text-base text-slate-600 max-w-md">
            The link may be out of date or mistyped. Here are a few places that
            might help &mdash; or book a consultation with Krina directly.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild data-testid="not-found-home-btn" className="rounded-full">
              <Link to="/"><Home size={16} /> Back to home</Link>
            </Button>
            <Button
              variant="outline"
              onClick={openBooking}
              data-testid="not-found-book-btn"
              className="rounded-full"
            >
              Book a consultation <ArrowRight size={16} />
            </Button>
          </div>

          <ul className="mt-10 grid gap-2" data-testid="not-found-links">
            {QUICK_LINKS.map(({ to, label, icon: Icon }) => (
              <li key={to}>
                <Link
                  to={to}
                  className="group flex items-center gap-3 rounded-xl border border-teal-100 bg-white px-4 py-3 text-slate-800 transition-colors hover:border-teal-300 hover:bg-teal-50"
                  data-testid={`not-found-link-${to.replace(/\//g, '-')}`}
                >
                  <Icon size={18} className="text-teal-700" />
                  <span className="font-medium">{label}</span>
                  <ArrowRight
                    size={15}
                    className="ml-auto text-teal-500 opacity-0 transition-opacity group-hover:opacity-100"
                  />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </main>
    </>
  );
}
